/**
 * EngagementScoreHistogram - Displays engagement score distribution
 * 
 * Shows user count distribution across 5 engagement score buckets:
 * - 0-0.2: Very Low Engagement
 * - 0.2-0.4: Low Engagement
 * - 0.4-0.6: Medium Engagement
 * - 0.6-0.8: High Engagement
 * - 0.8-1.0: Very High Engagement
 * 
 * Features:
 * - Responsive design
 * - Accessible (ARIA labels, keyboard navigation)
 * - Hover tooltips with exact counts
 * - Color-coded bars (gradient from red to green)
 */

import React from 'react';
import BarChart from './BarChart';
import './EngagementScoreHistogram.css';

const EngagementScoreHistogram = ({
  data = {},
  loading = false,
  className = ''
}) => {
  // Define bucket labels and colors
  const buckets = [
    { label: '0-0.2', key: '0-0.2', color: '#EF4444', description: 'Very Low Engagement' },
    { label: '0.2-0.4', key: '0.2-0.4', color: '#F59E0B', description: 'Low Engagement' },
    { label: '0.4-0.6', key: '0.4-0.6', color: '#F59E0B', description: 'Medium Engagement' },
    { label: '0.6-0.8', key: '0.6-0.8', color: '#10B981', description: 'High Engagement' },
    { label: '0.8-1.0', key: '0.8-1.0', color: '#10B981', description: 'Very High Engagement' }
  ];

  // Transform data for BarChart
  const chartData = {
    labels: buckets.map(b => b.label),
    datasets: [{
      label: 'Users',
      data: buckets.map(b => data[b.key] || 0),
      backgroundColor: buckets.map(b => b.color),
      borderColor: buckets.map(b => b.color),
      borderWidth: 0,
      borderRadius: 4
    }]
  };

  // Custom tooltip to show bucket description
  const chartOptions = {
    plugins: {
      tooltip: {
        callbacks: {
          title: (context) => {
            const index = context[0].dataIndex;
            return buckets[index].description;
          },
          label: (context) => {
            const count = context.parsed.y;
            const total = chartData.datasets[0].data.reduce((sum, val) => sum + val, 0);
            const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
            return `${count} users (${percentage}%)`;
          }
        }
      },
      legend: {
        display: false // Hide legend since colors are self-explanatory
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: (value) => {
            // Only show integer values
            return Number.isInteger(value) ? value : '';
          }
        },
        title: {
          display: true,
          text: 'Number of Users',
          font: {
            size: 12,
            weight: '600'
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Engagement Score Range',
          font: {
            size: 12,
            weight: '600'
          }
        }
      }
    }
  };

  // Calculate summary statistics
  const totalUsers = Object.values(data).reduce((sum, count) => sum + count, 0);
  const lowEngagement = (data['0-0.2'] || 0) + (data['0.2-0.4'] || 0);
  const highEngagement = (data['0.6-0.8'] || 0) + (data['0.8-1.0'] || 0);

  return (
    <div className={`engagement-score-histogram ${className}`}>
      <div className="engagement-score-histogram__header">
        <h3 className="engagement-score-histogram__title">Engagement Score Distribution</h3>
        <p className="engagement-score-histogram__description">
          User distribution across engagement score ranges
        </p>
      </div>

      <div className="engagement-score-histogram__chart">
        <BarChart
          data={chartData}
          loading={loading}
          height={300}
          options={chartOptions}
          emptyMessage="No engagement data available"
          showLegend={false}
        />
      </div>

      {!loading && totalUsers > 0 && (
        <div className="engagement-score-histogram__summary">
          <div className="engagement-score-histogram__stat">
            <span className="engagement-score-histogram__stat-label">Total Users:</span>
            <span className="engagement-score-histogram__stat-value">{totalUsers}</span>
          </div>
          <div className="engagement-score-histogram__stat">
            <span className="engagement-score-histogram__stat-label">Low Engagement:</span>
            <span className="engagement-score-histogram__stat-value engagement-score-histogram__stat-value--warning">
              {lowEngagement} ({totalUsers > 0 ? ((lowEngagement / totalUsers) * 100).toFixed(1) : 0}%)
            </span>
          </div>
          <div className="engagement-score-histogram__stat">
            <span className="engagement-score-histogram__stat-label">High Engagement:</span>
            <span className="engagement-score-histogram__stat-value engagement-score-histogram__stat-value--success">
              {highEngagement} ({totalUsers > 0 ? ((highEngagement / totalUsers) * 100).toFixed(1) : 0}%)
            </span>
          </div>
        </div>
      )}

      <div className="engagement-score-histogram__legend">
        <h4 className="engagement-score-histogram__legend-title">Score Ranges:</h4>
        <ul className="engagement-score-histogram__legend-list">
          {buckets.map((bucket) => (
            <li key={bucket.key} className="engagement-score-histogram__legend-item">
              <span 
                className="engagement-score-histogram__legend-color"
                style={{ backgroundColor: bucket.color }}
                aria-hidden="true"
              ></span>
              <span className="engagement-score-histogram__legend-label">
                {bucket.label}: {bucket.description}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default EngagementScoreHistogram;
